import { useState } from "react";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { nexusApi } from "@/context/NexusAuthContext";
import { CATEGORIES } from "@/pages/nexus/meta";
import "@/pages/nexus/nexus.css";

const empty = {
  contact_name: "", contact_email: "", contact_phone: "", company: "",
  category: "", jurisdictions_text: "", regulator: "",
  budget_band: "", timeframe: "", notes: "", website: "",
};

const BUDGETS = ["< €100k", "€100k–€500k", "€500k–€2M", "€2M–€10M", "> €10M", "Confidential"];
const TIMEFRAMES = ["< 30 days", "30–90 days", "90–180 days", "> 180 days", "Opportunistic"];

export default function BuySide() {
  const { t } = useTranslation();
  const [form, setForm] = useState(empty);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await nexusApi.post("/buy-side-requests", {
        contact_name: form.contact_name,
        contact_email: form.contact_email,
        contact_phone: form.contact_phone,
        company: form.company,
        category: form.category,
        jurisdictions: form.jurisdictions_text.split(",").map((s) => s.trim()).filter(Boolean),
        regulator: form.regulator,
        budget_band: form.budget_band,
        timeframe: form.timeframe,
        notes: form.notes,
        website: form.website,
      });
      setSent(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      toast.error(err.response?.data?.detail || t("nexus.sell.couldNotSubmit"));
    } finally {
      setSending(false);
    }
  };

  if (sent) return (
    <div className="nx-container py-24 text-center max-w-2xl mx-auto" data-testid="buyside-sent">
      <CheckCircle2 size={40} className="text-[#7FB08A] mx-auto mb-5" />
      <h1 className="font-serif text-3xl md:text-4xl text-white mb-4">{t("nexus.buySide.sentTitle")}</h1>
      <p className="text-white/75 leading-relaxed">{t("nexus.buySide.sentBody")}</p>
    </div>
  );

  return (
    <div data-testid="nexus-buyside">
      <section className="nx-hero" style={{ padding: "72px 0 40px" }}>
        <div className="nx-container">
          <div className="nx-eyebrow mb-3">{t("nexus.buySide.eyebrow")}</div>
          <h1 className="font-serif text-4xl md:text-5xl text-white mb-4">{t("nexus.buySide.title")}</h1>
          <p className="text-white/75 text-lg max-w-2xl">{t("nexus.buySide.lead")}</p>
        </div>
      </section>
      <section className="nx-container py-12">
        <form onSubmit={submit} className="grid gap-6 md:grid-cols-2 max-w-4xl">
          <Field label={`${t("nexus.buySide.yourName")} *`} name="contact_name" value={form.contact_name} onChange={onChange} required />
          <Field label={`${t("nexus.buySide.email")} *`} name="contact_email" type="email" value={form.contact_email} onChange={onChange} required />
          <Field label={t("nexus.buySide.phone")} name="contact_phone" value={form.contact_phone} onChange={onChange} />
          <Field label={t("nexus.buySide.companyFund")} name="company" value={form.company} onChange={onChange} />

          <div>
            <label className="nx-label">{t("nexus.buySide.category")}</label>
            <select name="category" value={form.category} onChange={onChange} className="nx-field">
              <option value="">{t("nexus.buySide.any")}</option>
              {CATEGORIES.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
            </select>
          </div>
          <Field label={t("nexus.buySide.targetJurisdictions")} name="jurisdictions_text" value={form.jurisdictions_text} onChange={onChange} placeholder={t("nexus.buySide.targetJurisdictionsPh")} />
          <Field label={t("nexus.buySide.regulator")} name="regulator" value={form.regulator} onChange={onChange} placeholder={t("nexus.buySide.regulatorPh")} />
          <div>
            <label className="nx-label">{t("nexus.buySide.budgetBand")}</label>
            <select name="budget_band" value={form.budget_band} onChange={onChange} className="nx-field">
              <option value="">{t("nexus.buySide.any")}</option>
              {BUDGETS.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </div>
          <div>
            <label className="nx-label">{t("nexus.buySide.timeframe")}</label>
            <select name="timeframe" value={form.timeframe} onChange={onChange} className="nx-field">
              <option value="">{t("nexus.buySide.any")}</option>
              {TIMEFRAMES.map((tf) => <option key={tf} value={tf}>{tf}</option>)}
            </select>
          </div>
          <div className="hidden md:block" />
          <div className="md:col-span-2">
            <label className="nx-label">{t("nexus.buySide.additionalNotes")} *</label>
            <textarea name="notes" required rows={5} maxLength={2000} className="nx-field" value={form.notes} onChange={onChange} placeholder={t("nexus.buySide.notesPh")} data-testid="buyside-notes" />
          </div>

          {/* honeypot */}
          <div aria-hidden="true" style={{ position: "absolute", left: "-10000px" }}>
            <input tabIndex={-1} autoComplete="off" type="text" name="website" value={form.website} onChange={onChange} />
          </div>

          <div className="md:col-span-2 pt-2">
            <button type="submit" className="nx-btn-primary" disabled={sending} data-testid="buyside-submit">
              {sending ? t("nexus.buySide.sending") : <>{t("nexus.buySide.submit")} <ArrowRight size={14} /></>}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

function Field({ label, ...props }) {
  return (
    <div>
      <label className="nx-label">{label}</label>
      <input className="nx-field" {...props} />
    </div>
  );
}
